import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import type {
  CheckExistenceResponse,
  MasterData,
  Patient,
  SavePayload,
} from './types';

export const patientsApi = {
  async getMasterData(siteType: string, clinicNumber: string) {
    const res = await apiClient.get<{ success: boolean; data: MasterData }>(
      clinicApiPath(siteType, clinicNumber, '/patients/master-data')
    );
    return res.data.data;
  },

  async getEditInit(siteType: string, clinicNumber: string, patientId: number) {
    const res = await apiClient.get<{ success: boolean; data: Patient }>(
      clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/edit-init`)
    );
    return res.data.data;
  },

  async search(siteType: string, clinicNumber: string, params: { keyword?: string; include_exclusion?: boolean }) {
    const queryParams = {
      ...params,
      include_exclusion: params.include_exclusion ? 1 : 0,
    };

    const res = await apiClient.get<{ success: boolean; data: { data: Patient[] } }>(
      clinicApiPath(siteType, clinicNumber, '/patients/search'),
      { params: queryParams }
    );
    return res.data.data.data;
  },

  async create(siteType: string, clinicNumber: string, payload: SavePayload) {
    const res = await apiClient.post<{ success: boolean; data: Patient }>(
      clinicApiPath(siteType, clinicNumber, '/patients'),
      payload
    );
    return res.data.data;
  },

  async update(siteType: string, clinicNumber: string, patientId: number, payload: SavePayload) {
    const res = await apiClient.put<{ success: boolean; data: Patient }>(
      clinicApiPath(siteType, clinicNumber, `/patients/${patientId}`),
      payload
    );
    return res.data.data;
  },

  async provisional(siteType: string, clinicNumber: string, patientId: number, payload: SavePayload) {
    const res = await apiClient.post<{ success: boolean; data: Patient }>(
      clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/provisional`),
      payload
    );
    return res.data.data;
  },

  async switchHistory(
    siteType: string,
    clinicNumber: string,
    patientId: number,
    params: { type: 'patient_information' | 'health_insurance_card' | 'medical_aid'; history_id: number }
  ) {
    const res = await apiClient.post<{ success: boolean; data: Patient }>(
      clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/histories/switch`),
      params
    );
    return res.data.data;
  },

  async bulkCreateHistory(siteType: string, clinicNumber: string, patientId: number, payload: SavePayload) {
    const res = await apiClient.post<{ success: boolean; data: Patient }>(
      clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/histories/bulk-create`),
      {
        patient_information: payload.patient_information,
        health_insurance_card: payload.health_insurance_card,
        medical_aid: payload.medical_aid,
        options: payload.options,
      }
    );
    return res.data.data;
  },

  async deleteHistory(
    siteType: string,
    clinicNumber: string,
    patientId: number,
    type: 'patient_information' | 'health_insurance_card' | 'medical_aid',
    historyId: number
  ) {
    const res = await apiClient.delete<{ success: boolean; data: Patient }>(
      clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/histories/${type}/${historyId}`)
    );
    return res.data.data;
  },

  async deletePatient(siteType: string, clinicNumber: string, patientId: number) {
    const res = await apiClient.delete<{ success: boolean; data: { deleted: boolean } }>(
      clinicApiPath(siteType, clinicNumber, `/patients/${patientId}`)
    );
    return res.data.data;
  },

  async checkExistence(siteType: string, clinicNumber: string, patientId: number, lastVisitYearMonth: string) {
    const res = await apiClient.get<{ success: boolean; data: CheckExistenceResponse }>(
      clinicApiPath(siteType, clinicNumber, `/patients/check-existence/${patientId}/${lastVisitYearMonth}`)
    );
    return res.data.data;
  },
};
